import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ChevronDown, ChevronUp, ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { gaEvent } from "../lib/analytics";
import { useT } from "../i18n/LanguageContext";

const STATES = ["Telangana", "Andhra Pradesh"];

// Illustrative per-kWh differential (grid tariff − WattZap rate), by state
const DIFFERENTIAL = { "Telangana": 2.4, "Andhra Pradesh": 2.2 };
// Illustrative solar offset % of daytime load, by state
const SOLAR_OFFSET_PCT = { "Telangana": 0.55, "Andhra Pradesh": 0.5 };
const DAYTIME_SHARE = 0.65; // % of monthly units consumed during solar hours

function fmt(n) {
  return Math.round(n).toLocaleString("en-IN");
}

export default function Estimator() {
  const t = useT();
  const [monthlyBill, setMonthlyBill] = useState("");
  const [connectedLoad, setConnectedLoad] = useState("");
  const [hoursPerDay, setHoursPerDay] = useState("");
  const [state, setState] = useState("Telangana");
  const [showMath, setShowMath] = useState(false);

  const [leadName, setLeadName] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const result = useMemo(() => {
    const bill = parseFloat(monthlyBill) || 0;
    if (bill <= 0) return null;
    // Assume a blended rate to estimate monthly units. Use ₹8/kWh for TS, ₹7.5 for AP (illustrative).
    const blended = state === "Telangana" ? 8.0 : 7.5;
    const units = bill / blended;
    const daytimeUnits = units * DAYTIME_SHARE;
    const solarUnits = daytimeUnits * SOLAR_OFFSET_PCT[state];
    const perUnitSavings = DIFFERENTIAL[state]; // ₹/kWh saved on solar-offset units
    const savings = solarUnits * perUnitSavings;

    // Add a modest EMS + bill scan uplift (illustrative): 2–4% of bill
    const emsLow = bill * 0.02;
    const emsHigh = bill * 0.04;

    const low = savings * 0.85 + emsLow;
    const high = savings * 1.1 + emsHigh;
    return { low, high, units, solarUnits, blended, perUnitSavings, emsLow, emsHigh };
  }, [monthlyBill, state]);

  async function submitLead(e) {
    e.preventDefault();
    if (!leadName || !leadPhone) {
      toast.error("Please add your name and phone number");
      return;
    }
    if (!consent) {
      toast.error(t("Please accept the privacy consent to continue."));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: leadName,
        company: leadCompany,
        phone: leadPhone,
        email: leadEmail,
        consent: true,
        bill_range: monthlyBill ? `~₹${fmt(parseFloat(monthlyBill))}/mo` : "",
        message: `Estimator submission from ${state}`,
        source: "estimator",
        monthly_bill: parseFloat(monthlyBill) || null,
        connected_load: parseFloat(connectedLoad) || null,
        operating_hours: parseFloat(hoursPerDay) || null,
        state,
      });
      setSubmitted(true);
      gaEvent("lead_submitted", { source: "estimator" });
      toast.success("We'll be in touch within one business day");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try WhatsApp?");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <section className="bg-[#071A2D] text-white pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#00D4FF] mb-3 inline-flex items-center gap-2">
            <Calculator size={14} /> {t("Savings estimator")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">{t("How much can")} <span className="text-gradient-brand">WattZap</span> {t("cut from your bill?")}</h1>
          <p className="mt-5 text-white/75 max-w-2xl">{t("Five inputs, one indicative monthly savings range. No sign-up needed to see the number.")}</p>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Inputs */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-100 shadow-card-soft p-7 lg:p-9 bg-white">
              <h2 className="font-heading text-2xl text-graphite mb-6">{t("Your facility")}</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Average monthly bill (₹)")}</span>
                  <input data-testid="est-monthly-bill" type="number" min="0" value={monthlyBill} onChange={(e) => setMonthlyBill(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-graphite focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="e.g. 250000" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Connected load (kW)")}</span>
                  <input data-testid="est-connected-load" type="number" min="0" value={connectedLoad} onChange={(e) => setConnectedLoad(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-graphite focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="e.g. 180" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Operating hours / day")}</span>
                  <input data-testid="est-hours" type="number" min="0" max="24" value={hoursPerDay} onChange={(e) => setHoursPerDay(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-graphite focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="e.g. 12" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("State")}</span>
                  <select data-testid="est-state" value={state} onChange={(e) => setState(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-graphite focus:outline-none focus:ring-2 focus:ring-[#00D4FF] bg-white">
                    {STATES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </label>
              </div>

              <div className="mt-8">
                <button onClick={() => setShowMath((v) => !v)}
                  className="text-sm text-[#00D4FF] inline-flex items-center gap-1.5"
                  data-testid="est-math-toggle"
                >
                  {t("How we estimate this")} {showMath ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showMath && (
                  <div data-testid="est-math-panel" className="mt-4 rounded-xl bg-mist p-5 text-sm text-slate-700 leading-relaxed border border-slate-100">
                    <p><b>Step 1.</b> {t(`Estimate monthly units from bill using a blended rate of ₹${state === "Telangana" ? "8.0" : "7.5"}/kWh (illustrative).`)}</p>
                    <p className="mt-2"><b>Step 2.</b> {t(`Assume ${Math.round(DAYTIME_SHARE * 100)}% of units are consumed during solar hours.`)}</p>
                    <p className="mt-2"><b>Step 3.</b> {t(`Assume WattZap solar offsets ${Math.round(SOLAR_OFFSET_PCT[state] * 100)}% of daytime units for a ${state} facility.`)}</p>
                    <p className="mt-2"><b>Step 4.</b> {t(`Apply a per-unit differential (grid − WattZap) of ₹${DIFFERENTIAL[state]}/kWh.`)}</p>
                    <p className="mt-2"><b>Step 5.</b> {t("Add a 2–4% EMS Lite + Bill Scan optimisation uplift on total bill.")}</p>
                    <p className="mt-3 italic text-slate-500">{t("Indicative estimate only. Actual savings depend on your tariff, load profile and site survey.")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#071A2D] text-white p-7 lg:p-9 border border-white/10 sticky top-24">
              <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-3">{t("Indicative monthly savings")}</div>
              {result ? (
                <>
                  <div data-testid="est-result-range" className="font-heading text-4xl">
                    ₹{fmt(result.low)} <span className="text-white/50 text-2xl">–</span> ₹{fmt(result.high)}
                    <div className="text-sm text-white/60 font-sans font-normal mt-2">{t("per month, indicative")}</div>
                  </div>
                  <div className="mt-6 text-sm text-white/70 space-y-1.5">
                    <div>{t("• Est. monthly units:")} <b className="text-white">{fmt(result.units)} kWh</b></div>
                    <div>{t("• Solar-offset units:")} <b className="text-white">{fmt(result.solarUnits)} kWh</b></div>
                    <div>{t("• Per-unit differential:")} <b className="text-white">₹{result.perUnitSavings}/kWh</b></div>
                  </div>
                </>
              ) : (
                <div className="text-white/60 text-sm">{t("Enter your monthly bill to see an estimate.")}</div>
              )}
              <div className="mt-6 text-xs text-white/45">
                {t("Indicative estimate only. Actual savings depend on your tariff, load profile and site survey.")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead form pre-filled */}
      <section className="bg-mist py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl bg-white border border-slate-100 p-7 lg:p-10 shadow-card-soft">
            {!submitted ? (
              <>
                <h3 className="font-heading text-2xl text-graphite">{t("Get the detailed savings report")}</h3>
                <p className="mt-2 text-sm text-slate-600">{t("We'll audit your last 12 months of bills against your load profile — free, no obligation.")}</p>
                <form onSubmit={submitLead} className="mt-6 grid sm:grid-cols-2 gap-4">
                  <input required data-testid="est-lead-name" placeholder={t("Your name")} value={leadName} onChange={(e) => setLeadName(e.target.value)}
                    className="rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  <input data-testid="est-lead-company" placeholder={t("Company / mill")} value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)}
                    className="rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  <input required data-testid="est-lead-phone" placeholder={t("Mobile (10-digit)")} value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)}
                    className="rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  <input data-testid="est-lead-email" type="email" placeholder={t("Email (optional)")} value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)}
                    className="rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  <label className="sm:col-span-2 flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed cursor-pointer">
                    <input type="checkbox" data-testid="est-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-[#00D4FF]" />
                    <span>
                      {t("I consent to WattZap Energy Solutions Pvt. Ltd. collecting and using the above details to contact me about my energy-savings enquiry via phone, WhatsApp or email, as described in the")}{" "}
                      <Link to="/privacy" className="text-[#0092b0] underline" data-testid="est-consent-privacy-link">{t("Privacy Policy")}</Link>.
                    </span>
                  </label>
                  <button disabled={submitting} data-testid="est-submit" type="submit" className="sm:col-span-2 btn-gradient inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm disabled:opacity-60">
                    {submitting ? t("Submitting...") : (<>{t("Get my savings report")} <ArrowRight size={16} /></>)}
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="est-success" className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-[#21F59B]/15 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="text-[#21F59B]" />
                </div>
                <h3 className="font-heading text-2xl text-graphite mt-4">{t("You're on the list.")}</h3>
                <p className="mt-2 text-sm text-slate-600">{t("Our team will WhatsApp you within one business day with next steps.")}</p>
                <a href="https://wa.me/918639602606" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#21F59B] text-[#071A2D] px-5 py-3 rounded-full font-medium text-sm">
                  <MessageCircle size={16} /> {t("Or ping us on WhatsApp")}
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
